import { useEffect, useMemo, useState } from "react";
import { apiFetch, useAuth } from "../../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Working-company choices for the employee Add/Edit modals.
 *
 * - Real `admin` / `superadmin` accounts see ALL working companies of the
 *   client, fetched from the canonical
 *   `GET /api/company-config/:clientName/detect-companies` endpoint (the same
 *   source used by Company Configuration and the Departments admin page).
 * - Promoted admin-view accounts (DB role manager/user with an admin level)
 *   only see the companies already visible to them: the `companies` prop the
 *   parent page derives from its visibility-scoped employees list
 *   (`/employees/visibility`).
 *
 * @param {Object}   options
 * @param {string[]} options.companies  Visibility-scoped company names from the parent page.
 * @param {string}   [options.clientName] Client name override (falls back to localStorage).
 */
export const useWorkingCompanyOptions = ({ companies = [], clientName } = {}) => {
  const { user } = useAuth();

  const role = String(
    user?.role || localStorage.getItem("role") || ""
  ).toLowerCase();
  const isRealAdmin = role === "admin" || role === "superadmin";

  const client = clientName || localStorage.getItem("clientName") || "";

  const [allClientCompanies, setAllClientCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  useEffect(() => {
    // Only real admins may list every company under the client.
    if (!isRealAdmin || !client) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setCompaniesLoading(true);
        const res = await apiFetch(
          `${BASE_URL}/api/company-config/${encodeURIComponent(client)}/detect-companies`
        );
        const json = await res.json().catch(() => null);
        if (!res.ok || json?.success === false) {
          throw new Error(json?.message || "Failed to detect companies");
        }
        const raw = Array.isArray(json?.data?.companies)
          ? json.data.companies
          : Array.isArray(json?.companies)
          ? json.companies
          : [];
        const names = raw
          .map((c) => (typeof c === "string" ? c : c?.companyName))
          .filter((name) => typeof name === "string" && name.trim());
        if (!cancelled) setAllClientCompanies([...new Set(names)].sort());
      } catch (err) {
        console.error("[WorkingCompany] detect-companies failed:", err);
      } finally {
        if (!cancelled) setCompaniesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isRealAdmin, client]);

  // Visibility-scoped fallback (and the only source for promoted accounts).
  const scopedCompanies = useMemo(
    () =>
      [
        ...new Set(
          (companies || []).filter(
            (name) => typeof name === "string" && name.trim()
          )
        ),
      ].sort(),
    [companies]
  );

  const companyOptions =
    isRealAdmin && allClientCompanies.length > 0
      ? allClientCompanies
      : scopedCompanies;

  return { companyOptions, companiesLoading, isRealAdmin };
};

export default useWorkingCompanyOptions;
